<template>
  <v-container
    id="tt-scrollable-container"
    ref="scrollableContainer"
    fluid
    :class="[{'theme--dark':$vuetify.theme.dark}, 'h-100', 'pa-0']"
    style="overflow-x: scroll; z-index: 1"
  >
    <status-context-menu
      v-if="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :reservation="contextMenu.reservation"
      :visible="contextMenu.visible"
      @close="contextMenu.visible = false"
    />
    <div
      :style="`--tt-slot-width: ${slotWidth}px`"
      :class="[{'condensed': zoom < 1}, 'tt-container']"
    >
      <!-- ~~~~~~ HEADER -->
      <div
        :class="[
          {'is--grabbing': headerDragScrollOrigin !== undefined},
          {'zoom-50': zoom < 0.75},
          'tt-header'
        ]"
        @mousemove="headerMouseover"
        @mouseup="headerMouseup"
        @mousedown="headerMousedown"
      >
        <div class="tt-dummy-slot" />
        <div
          v-for="(timeSlot, i) in timeSlots"
          :key="`header_${i}`"
          class="tt-slot"
        >
          <span
            v-if="i % 2 === 0"
            class="tt-slot-text"
          >{{ $localized.hourMinTextFrom24hr(timeSlot.time) }}</span>
        </div>
        <time-table-indicator
          :slot-width="slotWidth"
          :aside-width="asideWidth"
          :timeline-start="timelineStart"
          :timeline-end="timelineEnd"
        />
      </div>
      <!-- ~~~~~~ ASIDE MENU -->
      <div class="tt-aside">
        <template v-for="(tab, i) in tabs">
          <div :key="`aside_heading_grp_${i}`">
            <div
              :key="`aside_heading_${i}`"
              class="tt-heading"
            >
        &nbsp;
            </div>
            <div
              v-for="(tabItem, j) in tab.tabItems"
              :key="`aside_row_${i}_${j}`"
              class="tt-row"
            >
              <div class="tt-row-tabitem">
                {{ tabItem.no }}
              </div>
              <div
                v-if="!tabItem.isDummy"
                class="tt-row-seats"
              >
                <sup>{{ tabItem.seats }}</sup>
              </div>
            </div>
          </div>
        </template>
      </div>
      <!-- ~~~~~~ TIMETABLE CONTENT -->
      <div class="tt-content">
        <template v-for="(tab, i) in tabs">
          <!-- Section Header -->
          <div
            :key="`content_header_${i}`"
            class="tt-inline-header"
          >
            <div class="tt-inline-header--inner">
              {{ tab.name }}
            </div>
          </div>
          <!-- Time Row -->
          <div :key="`content_tabitemgrp_${i}`">
            <div
              v-for="(tabItem, j) in tab.tabItems"
              :key="`content_time_${i}_${j}`"
              class="tt-time-row"
              :data-tabitemid="tabItem.id"
              :data-tabid="tab.id"
              @mousedown="rowMouseDown"
              @mousemove="rowMouseMoved"
              @mouseup="rowMouseUp"
              @mouseleave="mouseLeave"
              @touchstart="touchStart"
              @touchmove="touchMoved"
              @touchend="touchEnd"
            >
              <template v-if="tabItem === tab.tabItems[0]">
                <time-table-closed-slot
                  v-for="closedSlot in closedSlots"
                  :key="`closed_slot_${tab.id}_${closedSlot.id}`"
                  :begin="closedSlot.begin"
                  :end="closedSlot.end"
                  :row-span="tab.tabItems.length"
                  :timeline-start="timelineStart"
                  :row-height="rowHeight"
                  :slot-width="slotWidth"
                />
              </template>

              <template v-for="r in reservationsByTabItem.get(tabItem.id)">
                <time-table-tooltip
                  :key="`content_res_tooltip_${r.id}`"
                  :reservation="r"
                >
                  <template #activator="{createPopover, hidePopover}">
                    <time-table-event
                      :key="`content_res_${r.id}`"
                      :reservation="r"
                      :data-resid="r.id"
                      :row-height="rowHeight"
                      :slot-width="slotWidth"
                      :row-span="rowSpanForEvent(r, tabItem)"
                      :timeline-start="timelineStart"
                      :selected="selected(r.id)"
                      :overbooked="overbooked.get(r.id)"
                      @context-menu="showContextMenu"
                      @click-event="eventClicked"
                      @show-tooltip="createPopover"
                      @hide-tooltip="hidePopover"
                    />
                  </template>
                </time-table-tooltip>
              </template>
              <time-table-closed-slot
                v-if="tabItem === tab.tabItems[0]"
                :begin="timelineEnd"
                :row-span="tab.tabItems.length"
                :timeline-start="timelineStart"
                :row-height="rowHeight"
                :slot-width="slotWidth"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </v-container>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Tab from '@/model/Tab';
import Reservation from '@/model/Reservation';
import TabItem from '@/model/TabItem';
import { mapActions } from 'vuex';
import DragDrop from '@/components/views/tables/mixins/drag-drop';
import DragNewReservation from '@/components/views/tables/mixins/drag-new-reservation';
import DayPartMixin from '@/components/views/tables/mixins/day-part';
import { tryCloseEditReservation, tryEditReservation } from '@/services/reservation-edit-utils';
import TimeSlot from '@/model/TimeSlot';
import { isOngoingReservation } from '@/services/reservation-utils';
import StatusContextMenu from '@/components/reservation/StatusContextMenu.vue';
import TimeTableEvent from '@/components/views/tables/TimeTableEvent.vue';
import TimeTableClosedSlot from '@/components/views/tables/TimeTableClosedSlot.vue';
import TimeTableIndicator from '@/components/views/tables/TimeTableIndicator.vue';
import TimeTableTooltip from '@/components/views/tables/TimeTableTooltip.vue';

const baseSlotWidth = 60;

export default Vue.extend({
  name: 'TimeTableGrid',
  components: {
    TimeTableEvent, TimeTableClosedSlot, TimeTableIndicator, StatusContextMenu, TimeTableTooltip,
  },
  mixins: [DayPartMixin, DragDrop, DragNewReservation],
  props: {
    zoom: { type: Number, required: false, default: 1 },
    tabs: { type: Array as PropType<Array<Tab>>, required: true },
    reservations: { type: Array as PropType<Array<Reservation>>, required: true },
    timeSlots: { type: Array as PropType<Array<TimeSlot>>, required: true },
    timelineStart: { type: Date, required: true },
    timelineEnd: { type: Date, required: true },
    closedSlots: { type: Array as PropType<Array<{id: number, begin: Date, end: Date}>>, required: true },
  },
  data() {
    return {
      headerDragX: 0,
      headerDragScrollOrigin: undefined as number | undefined,
      contextMenu: {
        reservation: null as null | Reservation,
        visible: false,
        x: 0,
        y: 0,
      },
    };
  },
  computed: {
    rowHeight(): number {
      return this.zoom < 1 ? 28 : 36;
    },
    slotWidth(): number {
      const width = Math.floor(this.zoom * baseSlotWidth);
      return width % 2 === 0 ? width : width + 1;
    },
    asideWidth(): number {
      return this.zoom < 1 ? 60 : 70;
    },
    reservationsInt(): Reservation[] {
      return this.$data.temporaryReservation !== null
        ? [...this.reservations, this.$data.temporaryReservation] : [...this.reservations];
    },
    reservationsByTabItem(): Map<number, Reservation[]> {
      const map = new Map<number, Reservation[]>();

      this.reservationsInt.forEach((r) => {
        for (let i = 0; i < r.tabItems.length; i += 1) {
          const ti = r.tabItems[i];
          if (i > 0 && r.tab !== null) {
            const tidx = r.tab.tabItems.indexOf(ti);
            if (tidx < 0 || r.tabItems[i - 1] !== r.tab.tabItems[tidx - 1]) {
              map.set(ti.id, (map.get(ti.id) ?? []).concat(r).sort((a, b) => a.tabItems.length - b.tabItems.length));
            }
          } else {
            map.set(ti.id, (map.get(ti.id) ?? []).concat(r).sort((a, b) => b.tabItems.length - a.tabItems.length));
          }
        }
      });
      return map;
    },
    overbooked(): Map<number, boolean> {
      const dayOverbookedFlagsMap = this.$tstore.getters.dayOverbookedFlagsForReservationId as Map<number, boolean>;
      return dayOverbookedFlagsMap;
    },
  },
  watch: {
    zoom(current: number, prev: number) {
      // console.log(`zoomOut: ${current < prev}, slotWidth: ${this.slotWidth}, c: ${current}, p: ${prev}`);
      const scrollValue = document.getElementById('tt-scrollable-container')!.scrollLeft;
      const prevSlot = scrollValue / (prev * baseSlotWidth);
      document.getElementById('tt-scrollable-container')!.scrollLeft = prevSlot * this.slotWidth;
    },
  },
  methods: {
    ...mapActions(['editReservation', 'sendReservation', 'setReservationFromContextStatusChange']),
    selected(rid: number): boolean {
      return rid === 0 || this.$tstore.getters.editReservation?.id === rid;
    },
    async eventClicked(r: Reservation, evt: any) {
      if (r.id !== 0 && this.$tstore.getters.editReservation?.id === r.id) {
        await tryCloseEditReservation();
        return;
      }

      const isOngoing = isOngoingReservation(r);
      if (isOngoing) {
        this.showContextMenu(r, evt);
      } else {
        await tryEditReservation(this.$router, r);
      }
    },
    showContextMenu(r: Reservation, evt: MouseEvent) {
      this.contextMenu.x = evt.clientX;
      this.contextMenu.y = evt.clientY;
      this.contextMenu.reservation = r;
      this.contextMenu.visible = true;
    },
    rowSpanForEvent(r: Reservation, tabItem: TabItem): number {
      if (!r.tab) return 1;

      const startIndex = r.tabItems.findIndex((ti) => ti.id === tabItem.id);
      const tStartIndex = r.tab.tabItems.findIndex((ti) => ti.id === tabItem.id);

      let rowSpan = 1;
      for (let i = startIndex + 1; i < r.tabItems.length; i += 1) {
        if (r.tabItems[i] !== r.tab.tabItems[tStartIndex + i - startIndex]) break;
        rowSpan += 1;
      }

      return rowSpan;
    },
    headerMouseup(evt: MouseEvent) {
      this.headerDragScrollOrigin = undefined;
    },
    headerMousedown(evt: MouseEvent) {
      const scrollElm = (this.$refs.scrollableContainer as HTMLElement);
      this.headerDragX = evt.screenX;
      this.headerDragScrollOrigin = scrollElm.scrollLeft;
    },
    headerMouseover(evt: MouseEvent) {
      if (evt.buttons === 0 || this.headerDragScrollOrigin === undefined) { return; }
      const scrollElm = (this.$refs.scrollableContainer as HTMLElement);
      scrollElm.scrollLeft = this.headerDragScrollOrigin + (this.headerDragX - evt.screenX);
    },
  },
});
</script>

<style lang="scss">
  @import "./scss/_styles.scss";
</style>
